import { Suspense, useMemo } from 'react';
import {
  Box,
  Page,
  PageBody,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderIconTile,
  PageHeaderTabs,
  PageHeaderTitle,
  TabNav,
  TabNavList,
  TabNavTab,
} from '@customerio/pluma-components/react';
import { useQueryState } from 'nuqs';
import { usePageViewTracking } from '@/hooks/use-page-view-tracking';
import { isEmptyString } from '@/utils/string';
import { useAnalytics } from '@/services/analytics';
import { DocumentTitle } from '@/components/page/document-title';
import { LoadingPage } from '@/components/page/loading';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import type { AttributeScope } from '@/services/db/data-index/entity';
import { DataIndexTable } from './components/data-index-table';
import { ExportMenu } from './components/export-menu';
import { SendEventButton } from './components/send-event-button';
import { BulkEditBar } from './components/bulk-edit-bar';
import { useDataIndexBulkActions } from './bulk';
import { toTableRow } from './utils/table-row';
import { ObjectTypeSelect } from './components/object-type-select';
import {
  useDataIndexList,
  useDataIndexRowWrites,
  useDeletedRows,
  useEnabledObjectTypes,
  useEnabledObjectTypesOptional,
  useRowSelection,
} from './hooks';
import { activeTabFor, DATA_INDEX_TABS, dataIndexListPath } from './urls';
import type { DataIndexType } from './utils/types';

/**
 * The data index's type list: every attribute or event a workspace has seen,
 * with what wrote it, what it is tagged with, and how much of the workspace
 * carries it.
 */
type DataIndexListPageProps = {
  type: DataIndexType;
};

/**
 * The view event the page this replaces fires on every transition into a
 * browser.
 *
 * Keyed on the path rather than the whole URL: paging and filtering are the
 * same view of the same browser, and firing again for each would drown the
 * one signal the event carries. Which browser was opened, and whether a
 * search was narrowing it when it was.
 */
function useDataIndexViewTracking(type: DataIndexType, isFilteredByName: boolean) {
  const analytics = useAnalytics();

  usePageViewTracking(
    () => {
      void analytics.track('logs', 'dataIndexViewed', {
        'Data Type': type === 'events' ? 'Events' : 'Attributes',
        'Filtered By Name': isFilteredByName ? 'Yes' : 'No',
      });
    },
    true,
    { ignoreSearch: true },
  );
}

export default function DataIndexListPage({ type }: DataIndexListPageProps) {
  const workspaceId = requireWorkspaceId();
  // Read straight from the URL rather than from the list's own state: a
  // shared or bookmarked link already carries the search, and the event
  // would otherwise report every narrowed view as unnarrowed.
  const [search] = useQueryState('search');
  useDataIndexViewTracking(type, !isEmptyString(search ?? ''));

  return (
    <Page>
      <DocumentTitle title="Data index" />
      <DataIndexHeader type={type} workspaceId={workspaceId} />
      <PageBody>
        <Suspense fallback={<LoadingPage />}>
          <DataIndexListBody type={type} />
        </Suspense>
      </PageBody>
    </Page>
  );
}

type DataIndexHeaderProps = {
  type: DataIndexType;
  workspaceId: number;
};

function DataIndexHeader({ type, workspaceId }: DataIndexHeaderProps) {
  const activeTab = activeTabFor(type);
  const objectTypes = useEnabledObjectTypesOptional();

  return (
    <PageHeader>
      <PageHeaderIconTile name="data-index" />
      <PageHeaderTitle>Data index</PageHeaderTitle>
      <PageHeaderDescription>
        A snapshot of all the attributes and events in your Customer.io account.
      </PageHeaderDescription>
      <PageHeaderTabs>
        <TabNav aria-label="Data index">
          <TabNavList>
            {DATA_INDEX_TABS.map((tab) => (
              <TabNavTab
                key={tab.type}
                href={dataIndexListPath(String(workspaceId), tab.type)}
                isActive={activeTab === tab.type}
              >
                {tab.label}
              </TabNavTab>
            ))}
          </TabNavList>
        </TabNav>
      </PageHeaderTabs>
      <PageHeaderActions>
        <ExportMenu workspaceId={workspaceId} objectTypes={objectTypes} />
        {type === 'events' ? <SendEventButton workspaceId={workspaceId} source="Data Index Event List" /> : null}
      </PageHeaderActions>
    </PageHeader>
  );
}

type DataIndexListBodyProps = {
  type: DataIndexType;
};

function DataIndexListBody({ type }: DataIndexListBodyProps) {
  const list = useDataIndexList(type);
  const objectTypes = useEnabledObjectTypes();
  const environment = useEnvironmentOptional();
  const taxonomy = useProductTaxonomy();
  const selection = useRowSelection(list.rowSetKey);
  const deleted = useDeletedRows(list.deletionScopeKey);
  const writes = useDataIndexRowWrites(list.workspaceId, list.kind, list.scope);

  // The description each row shows is resolved here, not per cell, because
  // editing one starts from what it shows.
  const terms = useMemo(
    () => ({
      campaignsPlural: taxonomy.term('campaigns.plural'),
      personSingular: taxonomy.term('people.singular'),
      personPlural: taxonomy.term('people.plural'),
      customerSingular: taxonomy.term('customers.singular'),
    }),
    [taxonomy],
  );
  const allRows = useMemo(
    () => list.rows.map((row) => toTableRow(row, list.kind, terms)),
    [list.rows, list.kind, terms],
  );

  const bulk = useDataIndexBulkActions({
    workspaceId: list.workspaceId,
    kind: list.kind,
    scope: list.scope,
    rows: allRows,
    identifiers: environment.data?.environment?.identifiers,
    selection: selection.rows,
    onSelectionChange: selection.setRows,
    onDeleted: deleted.add,
  });

  const rows = useMemo(() => allRows.filter((row) => !deleted.names.includes(row.id)), [allRows, deleted.names]);

  const objectTypeName = objectTypeNameOf(objectTypes, list.scope);

  return (
    <>
      {type !== 'events' && bulk.bar == null ? (
        <Box mb="100">
          <ObjectTypeSelect value={list.scopeSelection} objectTypes={objectTypes} onChange={list.setScopeSelection} />
        </Box>
      ) : null}
      {bulk.bar != null ? (
        <Box mb="100">
          <BulkEditBar {...bulk.bar} />
        </Box>
      ) : null}
      <DataIndexTable
        workspaceId={list.workspaceId}
        kind={list.kind}
        scope={list.scope}
        rows={rows}
        tags={list.tags}
        identifiers={environment.data?.environment?.identifiers}
        rowUsage={list.rowUsage}
        isRowUsageLoading={list.isRowUsageLoading}
        isTotalPeopleLoading={list.isTotalPeopleLoading}
        isProfileCountsFetching={list.isProfileCountsFetching}
        isObjectCountsLoading={list.isObjectCountsLoading}
        isLoading={list.isLoading}
        narrowing={list.narrowing}
        catalogue={list.catalogue}
        totals={list.totals}
        page={list.page}
        onPageChange={list.setPage}
        rowSelection={selection.rows}
        onRowSelectionChange={selection.setRows}
        profileCounts={list.profileCounts}
        totalPeople={list.totalPeople}
        objectCounts={list.objectCounts}
        totalObjects={list.totalObjects}
        objectTypeName={objectTypeName}
        onSaveDescription={writes.saveDescription}
        onSaveTags={writes.saveTags}
        bulkActions={bulk.actions}
        bulkEdit={bulk.bulkEdit}
      />
    </>
  );
}

/** What to call the objects a scope counts against, falling back to the generic noun. */
function objectTypeNameOf(objectTypes: readonly { id: string; name: string }[], scope: AttributeScope): string {
  if (scope.kind === 'profile') {
    return 'Objects';
  }
  return objectTypes.find((objectType) => Number(objectType.id) === scope.objectTypeId)?.name ?? 'Objects';
}
